import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconDownload,
  IconPrinter,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatMoney } from '@erp/shared';
import { ApiError } from '../api/client';
import { actsApi, type ActCreateInput, type ActUpdateInput } from '../api/acts';
import { unitsOfMeasureApi, vatRatesApi } from '../api/catalogs';
import { counterpartiesApi } from '../api/counterparties';
import { nomenclatureApi } from '../api/nomenclature';
import { organizationsApi } from '../api/organizations';
import { ActLinesEditor, emptyActLineRow, type ActLineRow } from '../components/ActLinesEditor';
import { useAuth } from '../auth/AuthContext';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

export function ActFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const canEditGlobally = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [organizationId, setOrganizationId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [date, setDate] = useState(today());
  const [basis, setBasis] = useState('');
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');
  const [signerPosition, setSignerPosition] = useState('');
  const [signerName, setSignerName] = useState('');
  const [comment, setComment] = useState('');
  const [footerText, setFooterText] = useState('');
  const [lines, setLines] = useState<ActLineRow[]>([emptyActLineRow()]);
  const [formError, setFormError] = useState<string[] | null>(null);

  const loadedFor = useRef<string | null>(null);

  const actQuery = useQuery({
    queryKey: ['act', id],
    queryFn: () => actsApi.get(id!),
    enabled: !isNew,
  });

  const organizations = useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: () => organizationsApi.list({ pageSize: 200 }),
  });
  const counterparties = useQuery({
    queryKey: ['counterparties', 'all'],
    queryFn: () => counterpartiesApi.list({ pageSize: 200 }),
  });
  const units = useQuery({ queryKey: ['units-of-measure'], queryFn: () => unitsOfMeasureApi.list() });
  const vatRates = useQuery({ queryKey: ['vat-rates'], queryFn: () => vatRatesApi.list() });
  const nomenclature = useQuery({
    queryKey: ['nomenclature', 'all'],
    queryFn: () => nomenclatureApi.list({ pageSize: 500 }),
  });

  const act = actQuery.data?.act ?? null;
  const canEditDocument = canEditGlobally;

  // Заполняем форму данными документа один раз при загрузке — иначе фоновый
  // рефетч перезаписывал бы то, что пользователь уже успел набрать.
  useEffect(() => {
    if (!act || loadedFor.current === act.id) return;
    loadedFor.current = act.id;

    setOrganizationId(act.organizationId);
    setCounterpartyId(act.counterpartyId);
    setDate(toDateInput(act.date));
    setBasis(act.basis ?? '');
    setPeriodFrom(toDateInput(act.periodFrom));
    setPeriodTo(toDateInput(act.periodTo));
    setSignerPosition(act.signerPosition ?? '');
    setSignerName(act.signerName ?? '');
    setComment(act.comment ?? '');
    setFooterText(act.footerText ?? '');
    setLines(
      act.lines.map((l) => ({
        key: l.id,
        nomenclatureId: l.nomenclatureId,
        name: l.name,
        article: l.article,
        unitId: l.unitId ?? '',
        quantity: l.quantity,
        price: l.price,
        vatRateId: vatRates.data?.items.find((r) => r.code === l.vatRateCode)?.id ?? '',
      })),
    );
  }, [act, vatRates.data]);

  const organizationOptions = (organizations.data?.items ?? []).map((o) => ({ value: o.id, label: o.shortName }));
  const counterpartyOptions = (counterparties.data?.items ?? []).map((c) => ({ value: c.id, label: c.shortName }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        date,
        basis: basis || null,
        periodFrom: periodFrom || null,
        periodTo: periodTo || null,
        signerPosition: signerPosition || null,
        signerName: signerName || null,
        comment: comment || null,
        footerText: footerText || null,
        lines: lines.map((l) => ({
          nomenclatureId: l.nomenclatureId,
          name: l.name.trim(),
          article: l.article || null,
          unitId: l.unitId || null,
          quantity: l.quantity,
          price: l.price,
          vatRateId: l.vatRateId,
        })),
      };
      if (isNew) {
        const payload: ActCreateInput = { ...body, organizationId, counterpartyId };
        return actsApi.create(payload);
      }
      return actsApi.update(id!, body satisfies ActUpdateInput);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['acts'] });
      void queryClient.invalidateQueries({ queryKey: ['act', data.act.id] });
      notifications.show({ color: 'green', message: 'Акт сохранён' });
      if (isNew) navigate(`/acts/${data.act.id}`, { replace: true });
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors: string[] = [];
    if (isNew && !organizationId) errors.push('Укажите организацию');
    if (isNew && !counterpartyId) errors.push('Укажите заказчика');
    if (!date) errors.push('Укажите дату');
    if (periodFrom && periodTo && periodFrom > periodTo) {
      errors.push('Окончание периода не может быть раньше начала');
    }
    if (lines.length === 0) errors.push('Добавьте хотя бы одну строку');
    lines.forEach((l, i) => {
      if (!l.name.trim()) errors.push(`Строка ${i + 1}: укажите наименование`);
      if (!l.vatRateId) errors.push(`Строка ${i + 1}: укажите ставку НДС`);
      if (!l.quantity.trim()) errors.push(`Строка ${i + 1}: укажите количество`);
      if (!l.price.trim()) errors.push(`Строка ${i + 1}: укажите цену`);
    });
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate();
  }

  const removeMutation = useMutation({
    mutationFn: () => actsApi.remove(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['acts'] });
      notifications.show({ color: 'green', message: 'Акт удалён' });
      navigate('/acts');
    },
    onError: (err) => {
      notifications.show({ color: 'red', message: err instanceof ApiError ? err.message : 'Не удалось удалить акт' });
    },
  });

  function handleRemove() {
    if (!act) return;
    if (!window.confirm(`Удалить акт № ${act.number}? Действие необратимо.`)) return;
    removeMutation.mutate();
  }

  if (!isNew && !act) {
    return <Text c="dimmed">Загрузка…</Text>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/acts')}>
            К списку
          </Button>
          <Title order={2}>{isNew ? 'Новый акт' : `Акт № ${act?.number ?? ''}`}</Title>
        </Group>

        {act && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              component="a"
              href={`/acts/${act.id}/print`}
              target="_blank"
            >
              Печать
            </Button>
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              onClick={() => window.open(`/acts/${act.id}/print?download=1`, '_blank')}
            >
              Скачать PDF
            </Button>
            {canEditDocument && (
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={16} />}
                onClick={handleRemove}
                loading={removeMutation.isPending}
              >
                Удалить
              </Button>
            )}
          </Group>
        )}
      </Group>

      {act && (
        <Text size="sm" c="dimmed">
          {act.organization.shortName} → {act.counterparty.shortName}
          {act.invoice && <> · на основании счёта № {act.invoice.number}</>}
        </Text>
      )}

      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          <Stack gap={2}>
            {formError.map((e, i) => (
              <Text key={i} size="sm">
                {e}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Организация и заказчик выбираются только при создании: у существующего
          акта смена организации сломала бы уже выданный номер, а смена
          заказчика превратила бы документ в другой. */}
      {isNew && (
        <Group grow align="flex-start">
          <Select
            label="Организация (Исполнитель)"
            placeholder="Выберите организацию"
            data={organizationOptions}
            value={organizationId || null}
            onChange={(v) => setOrganizationId(v ?? '')}
            searchable
            required
          />
          <Select
            label="Заказчик"
            placeholder="Выберите контрагента"
            data={counterpartyOptions}
            value={counterpartyId || null}
            onChange={(v) => setCounterpartyId(v ?? '')}
            searchable
            required
          />
        </Group>
      )}

      <Group grow align="flex-start">
        <TextInput
          label="Дата акта"
          type="date"
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
          disabled={!canEditDocument}
          required
        />
        <TextInput
          label="Период оказания услуг — с"
          type="date"
          value={periodFrom}
          onChange={(e) => setPeriodFrom(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="по"
          type="date"
          value={periodTo}
          onChange={(e) => setPeriodTo(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
      </Group>

      <TextInput
        label="Основание"
        placeholder="Договор, счёт или иное основание выполнения работ"
        value={basis}
        onChange={(e) => setBasis(e.currentTarget.value)}
        disabled={!canEditDocument}
      />

      <ActLinesEditor
        lines={lines}
        onChange={setLines}
        canEdit={canEditDocument}
        nomenclatureOptions={nomenclature.data?.items ?? []}
        units={units.data?.items ?? []}
        vatRates={vatRates.data?.items ?? []}
      />

      {act && (
        <Text size="sm" c="dimmed">
          Всего: {formatMoney(act.total)}
        </Text>
      )}

      <Group grow align="flex-start">
        <TextInput
          label="Подписант — должность"
          value={signerPosition}
          onChange={(e) => setSignerPosition(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="Подписант — ФИО"
          value={signerName}
          onChange={(e) => setSignerName(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
      </Group>

      <Textarea
        label="Текст под таблицей"
        description="Отметка о принятии работ без претензий — без неё акт не подтверждает приёмку"
        value={footerText}
        onChange={(e) => setFooterText(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={2}
      />

      <Textarea
        label="Комментарий"
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={1}
      />

      {canEditDocument && (
        <Group>
          <Button leftSection={<IconCheck size={16} />} onClick={handleSubmit} loading={saveMutation.isPending}>
            Сохранить
          </Button>
        </Group>
      )}
    </Stack>
  );
}
